import {
  documentToReactComponents,
  NodeRenderer,
  RenderMark,
  RenderNode,
} from '@contentful/rich-text-react-renderer'
import {Document, INLINES, MARKS, Table, Text as RichText} from '@contentful/rich-text-types'
import Image from 'next/image'

import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {BLOG_CONTENT_WIDTH} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {normalizeContentfulHref, parseImage} from '@/utils/contentful/helpers'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

export const renderMark: RenderMark = {
  [MARKS.BOLD]: (text) => <strong>{text}</strong>,
}

export const inlineFallbackRenderNode: RenderNode = {
  [INLINES.ENTRY_HYPERLINK]: (node, children) => children,
  [INLINES.ASSET_HYPERLINK]: (node, children) => children,
  [INLINES.EMBEDDED_ENTRY]: () => null,
}

export const renderHyperlink: NodeRenderer = (node, children) => (
  <>
    <a
      href={normalizeContentfulHref(node.data.uri)}
      target="_blank"
      rel="noopener noreferrer"
      className="link"
    >
      {children}
    </a>
    <style jsx>{`
      .link {
        color: inherit;
        font-weight: ${theme.fontWeight.semibold};
        text-decoration: underline;
        overflow-wrap: anywhere;
      }
    `}</style>
  </>
)

export const commonStyledOptions: RenderNode = {
  ...inlineFallbackRenderNode,
  ['embedded-asset-block']: (node) => {
    const fields = node.data.target.fields
    const {url, alt, width, height} = parseImage(node.data.target)
    return (
      <>
        <div className="image">
          <Image
            src={url}
            alt={alt}
            width={width}
            height={height}
            sizes={`(max-width: ${BLOG_CONTENT_WIDTH}) 100vw, ${BLOG_CONTENT_WIDTH}`}
            style={{display: 'block', width: '100%', height: 'auto'}}
          />
        </div>
        <div className="description">
          <Text variant={TextVariant.text14} color={theme.palette.text.light}>
            {fields.description}
          </Text>
        </div>
        <style jsx>{`
          .image {
            margin-top: ${theme.spacing[5]};
          }
          .description {
            margin-top: ${theme.spacing[2]};
          }
          @media ${MOBILE_SCREENSIZE} {
            .image {
              margin-top: ${theme.spacing[4]};
            }
          }
        `}</style>
      </>
    )
  },
  ['embedded-entry-block']: (node) => {
    if (node.data.target.sys.contentType.sys.id === 'definition') {
      const fields = node.data.target.fields
      return (
        <>
          <div className="definition">
            <Text variant={TextVariant.h5} element="h3" fontWeight={TextFontWeight.bold}>
              {fields.title}
            </Text>
            <div className="description">
              <Text>{fields.text}</Text>
            </div>
          </div>
          <style jsx>{`
            .definition {
              background-color: ${theme.palette.background.butter};
              padding: ${theme.spacing[5]};
              margin-top: ${theme.spacing[5]};
            }
            .description {
              margin-top: ${theme.spacing[3]};
            }
            @media ${MOBILE_SCREENSIZE} {
              .definition {
                padding: ${theme.spacing[4]};
                margin-top: ${theme.spacing[4]};
              }
              .description {
                margin-top: ${theme.spacing[2]};
              }
            }
          `}</style>
        </>
      )
    }
    if (node.data.target.sys.contentType.sys.id === 'table') {
      const table = node.data.target.fields.table.tableData
      return (
        <>
          <div className="table-wrapper">
            <table className="table">
              {table.map(
                (row: string[], i: number) =>
                  i === 0 && (
                    <thead key={row[0]}>
                      <tr key={row[0]}>
                        {row.map((cell: string) => (
                          <th key={cell}>
                            <Text variant={TextVariant.text14}>{cell}</Text>
                          </th>
                        ))}
                      </tr>
                    </thead>
                  ),
              )}
              <tbody>
                {table.map(
                  (row: string[], i: number) =>
                    i > 0 && (
                      <tr key={row[0]}>
                        {row.map((cell: string) => (
                          <td key={cell}>
                            <Text variant={TextVariant.text14}>{cell}</Text>
                          </td>
                        ))}
                      </tr>
                    ),
                )}
              </tbody>
            </table>
          </div>
          <style jsx>{`
            .table-wrapper {
              margin-top: ${theme.spacing[4]};
            }
            .table {
              width: 100%;
              border-collapse: separate;
              background-color: ${theme.palette.background.default};
              border-spacing: 0;
              border: 1px solid ${theme.palette.butter[200]};
              text-align: center;
            }
            th,
            td {
              padding: ${theme.spacing[2]};
            }
            th {
              font-weight: ${theme.fontWeight.semibold};
              background-color: ${theme.palette.background.butter};
            }
            tr + tr td {
              border-top: 1px solid ${theme.palette.butter[200]};
            }
            @media ${MOBILE_SCREENSIZE} {
              .table-wrapper {
                margin-top: ${theme.spacing[4]};
                margin-right: -${theme.spacing[3]};
                max-width: calc(100vw - ${theme.spacing[3]});
                overflow-x: auto;
              }
              th {
                min-width: 100px;
              }
            }
          `}</style>
        </>
      )
    }
    if (node.data.target.sys.contentType.sys.id === 'quote') {
      const fields = node.data.target.fields
      return (
        <>
          <div className="quote">
            <Text variant={TextVariant.h4} element="p" fontWeight={TextFontWeight.regular}>
              {fields.quoteText}
            </Text>
          </div>
          <style jsx>{`
            .quote {
              border-left: 2px solid ${theme.palette.primary.main};
              padding-left: ${theme.spacing[5]};
              margin-top: ${theme.spacing[6]};
            }
            @media ${MOBILE_SCREENSIZE} {
              .quote {
                padding-left: ${theme.spacing[3]};
                margin-top: ${theme.spacing[5]};
              }
            }
          `}</style>
        </>
      )
    }
  },
  ['table']: (node) => {
    const table = node as Table
    return (
      <>
        <div className="table-wrapper">
          <table className="table">
            <thead>
              {table.content.map(
                (row, rowIndex) =>
                  rowIndex === 0 && (
                    <tr key={rowIndex}>
                      {row.content.map((cell, cellIndex) => (
                        <th key={cellIndex}>
                          <Text variant={TextVariant.text14}>
                            {(cell.content[0].content[0] as RichText).value}
                          </Text>
                        </th>
                      ))}
                    </tr>
                  ),
              )}
            </thead>
            <tbody key="body">
              {table.content.map(
                (row, rowIndex) =>
                  rowIndex > 0 && (
                    <tr key={rowIndex}>
                      {row.content.map((cell, cellIndex) => (
                        <td key={cellIndex}>
                          {documentToReactComponents(
                            {nodeType: 'document', data: {}, content: cell.content} as Document,
                            {renderNode: commonStyledOptions},
                          )}
                        </td>
                      ))}
                    </tr>
                  ),
              )}
            </tbody>
          </table>
        </div>
        <style jsx>{`
          .table-wrapper {
            margin-top: ${theme.spacing[6]};
          }
          .table {
            width: 100%;
            border-collapse: collapse;
            background-color: ${theme.palette.background.default};
          }
          th,
          td {
            padding: ${theme.spacing[2]};
            vertical-align: top;
            border: 1px solid ${theme.palette.butter[400]};
          }
          th {
            font-weight: ${theme.fontWeight.semibold};
            background-color: ${theme.palette.background.butter};
          }
          td {
            ${theme.typography.text14.desktop};
          }
          td :global(p) {
            margin: 0;
          }
          td :global(p + p) {
            margin-top: ${theme.spacing[2.5]};
          }
          @media ${MOBILE_SCREENSIZE} {
            .table-wrapper {
              margin-top: ${theme.spacing[4]};
              margin-right: -${theme.spacing[3]};
              max-width: calc(100vw - ${theme.spacing[3]});
              overflow-x: auto;
            }
            th {
              min-width: 100px;
            }
          }
        `}</style>
      </>
    )
  },
  ['hyperlink']: (node, children) => (
    <a
      href={normalizeContentfulHref(node.data.uri)}
      target="_blank"
      rel="noreferrer"
      className="underline"
    >
      {children}
    </a>
  ),
  ['blockquote']: (node, children) => (
    <>
      <div className="quote">{children}</div>
      <style jsx>{`
        .quote {
          border-left: 2px solid ${theme.palette.primary.main};
          padding-left: ${theme.spacing[5]};
          margin-top: ${theme.spacing[6]};
        }
        @media ${MOBILE_SCREENSIZE} {
          .quote {
            padding-left: ${theme.spacing[3]};
            margin-top: ${theme.spacing[5]};
          }
        }
      `}</style>
    </>
  ),
}
